/**
 * The breadcrumb trail of every account-level screen, derived in one place from
 * the screen's view id. A page never writes its own trail: it names its view
 * (and, below a list, the record it is on) and <AccountBar> asks here. That is
 * what keeps the trail present and consistent on every page: Workspace first
 * (a link home), the list a form belongs to in the middle (a link back), the
 * page you are on last (plain text, aria-current="page").
 */
import { ACCOUNT_NAV } from '../components/account/accountNav';
import type { AccountView } from './accountRoutes';

export interface Crumb {
  label: string;
  /** Omit on the last crumb: it names the page you are on. */
  to?: string;
}

export const WORKSPACE_CRUMB: Crumb = { label: 'Workspace', to: '/overview' };

const NAV_BY_ID = new Map(ACCOUNT_NAV.flatMap(section => section.items).map(item => [item.id, item]));

/** A form page sits under the list it edits. */
const PARENT_LIST: Partial<Record<AccountView, AccountView>> = {
  'client-form': 'clients',
  'invoice-form': 'invoices',
  'invoice-payment-form': 'invoices',
  'client-quote-form': 'client-quotes',
  'lead-form': 'pipeline',
  'project-edit': 'projects',
  'user-edit': 'workspace-team',
};

/** What the page is called when the record gives no better name (a form that has not loaded yet). */
const FALLBACK_LEAF: Partial<Record<AccountView, string>> = {
  'client-form': 'Client',
  'invoice-form': 'Invoice',
  'invoice-payment-form': 'Record payment',
  'client-quote-form': 'Quote',
  'lead-form': 'New lead',
  'project-edit': 'Edit project',
  'user-edit': 'Edit user',
};

const labelOf = (view: AccountView): string => NAV_BY_ID.get(view)?.label ?? view;
const pathOf = (view: AccountView): string => NAV_BY_ID.get(view)?.path ?? WORKSPACE_CRUMB.to!;

/**
 * Workspace / [parent list /] [via... /] page.
 * `leaf` is the page's own name when it varies (the month on the Calendar, a record's number or name on a form);
 * `via` are crumbs between the list and the page (the invoice a payment belongs to).
 */
export function accountCrumbs(view: AccountView, leaf?: string, via: readonly Crumb[] = []): Crumb[] {
  const parent = PARENT_LIST[view];
  const name = leaf?.trim();
  if (!parent) {
    // A list page. With a leaf (Calendar: the month) the list itself becomes a link.
    return name
      ? [WORKSPACE_CRUMB, { label: labelOf(view), to: pathOf(view) }, { label: name }]
      : [WORKSPACE_CRUMB, { label: labelOf(view) }];
  }
  return [
    WORKSPACE_CRUMB,
    { label: labelOf(parent), to: pathOf(parent) },
    ...via,
    { label: name || FALLBACK_LEAF[view] || labelOf(view) },
  ];
}
